import type {
  BoxSpacing,
  Element,
  ElementAppearance,
  ElementPatch,
  FilterEffect,
  Paint,
  ShadowEffect,
  Stroke,
} from '@/components/canvas/document/publicTypes';

export function firstFill(appearance: ElementAppearance): Paint | undefined {
  return appearance.fills?.[0];
}

export function solidFill(color: string): Paint {
  return { type: 'solid', color };
}

export function paintToCss(paint: Paint | undefined): string | undefined {
  if (!paint) return undefined;
  if (paint.type === 'solid') return paint.color;
  if (paint.type === 'gradient') return paint.value;
  return undefined;
}

export function elementFillCss(element: Pick<Element, 'appearance'>): string | undefined {
  return paintToCss(firstFill(element.appearance));
}

export function firstStroke(appearance: ElementAppearance): Stroke | undefined {
  return appearance.strokes?.[0];
}

export function withFirstFill(color: string | undefined): Paint[] | undefined {
  return color ? [solidFill(color)] : undefined;
}

export function withFirstStroke(
  current: Stroke | undefined,
  patch: Partial<Stroke>,
  fallback: Stroke,
): Stroke[] {
  return [{ ...fallback, ...current, ...patch }];
}

export function firstShadow(appearance: ElementAppearance): ShadowEffect | undefined {
  return appearance.shadows?.[0];
}

export function blurRadius(element: Pick<Element, 'appearance'>): number | undefined {
  return element.appearance.filters?.find((filter) => filter.type === 'blur')?.radius;
}

export function withBlurFilter(
  filters: FilterEffect[] | undefined,
  radius: number,
): FilterEffect[] | undefined {
  const rest = filters?.filter((filter) => filter.type !== 'blur') ?? [];
  return radius > 0 ? [...rest, { type: 'blur', radius }] : (rest.length > 0 ? rest : undefined);
}

export function appendElementBlurFilter(base: string | undefined, element: Pick<Element, 'appearance'>): string | undefined {
  const radius = blurRadius(element);
  const blur = radius && radius > 0 ? `blur(${radius}px)` : '';
  if (base && blur) return `${base} ${blur}`;
  return base || blur || undefined;
}

export function shadowToCss(shadow: ShadowEffect | undefined): string | undefined {
  if (!shadow) return undefined;
  return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.color}`;
}

export type LayoutControlPatch = Partial<{
  x: number;
  y: number;
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  rotate3dX: number;
  rotate3dY: number;
  rotate3dZ: number;
}>;

export type BoxSizeControlPatch = Partial<{
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  fit: Element['size']['fit'];
  overflow: Element['size']['overflow'];
  scrollX: number;
  scrollY: number;
  padding: BoxSpacing | undefined;
}>;

export function layoutControlPatchToElementPatch(
  element: Element,
  partial: LayoutControlPatch,
): ElementPatch {
  const patch: ElementPatch = {};

  if (
    partial.x !== undefined ||
    partial.y !== undefined ||
    partial.rotation !== undefined ||
    partial.flipHorizontal !== undefined ||
    partial.flipVertical !== undefined ||
    partial.rotate3dX !== undefined ||
    partial.rotate3dY !== undefined ||
    partial.rotate3dZ !== undefined
  ) {
    patch.layout = {};
    if (partial.x !== undefined) patch.layout.x = partial.x;
    if (partial.y !== undefined) patch.layout.y = partial.y;
    if (partial.rotation !== undefined) patch.layout.rotation = partial.rotation;
    if (partial.flipHorizontal !== undefined) patch.layout.flipX = partial.flipHorizontal;
    if (partial.flipVertical !== undefined) patch.layout.flipY = partial.flipVertical;
    if (
      partial.rotate3dX !== undefined ||
      partial.rotate3dY !== undefined ||
      partial.rotate3dZ !== undefined
    ) {
      patch.layout.rotate3d = {
        ...element.layout.rotate3d,
        ...(partial.rotate3dX !== undefined ? { x: partial.rotate3dX } : {}),
        ...(partial.rotate3dY !== undefined ? { y: partial.rotate3dY } : {}),
        ...(partial.rotate3dZ !== undefined ? { z: partial.rotate3dZ } : {}),
      };
    }
  }

  return patch;
}

export function boxSizeControlPatchToElementPatch(partial: BoxSizeControlPatch): ElementPatch {
  const patch: ElementPatch = {};
  const sizeKeys: (keyof BoxSizeControlPatch)[] = [
    'width',
    'height',
    'minWidth',
    'minHeight',
    'maxWidth',
    'maxHeight',
    'fit',
    'overflow',
    'scrollX',
    'scrollY',
    'padding',
  ];
  if (sizeKeys.some((key) => partial[key] !== undefined)) {
    patch.size = {};
    for (const key of sizeKeys) {
      if (partial[key] !== undefined) {
        (patch.size as Record<string, unknown>)[key] = partial[key];
      }
    }
  }
  return patch;
}
